import { IntercomScreen } from '@getstep/sdk/dist/store/intercom/Screens'
import { ErrorMessage } from '@hookform/error-message'
import type { FunctionComponent } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { FullSsnInput, SSN_LENGTH } from '@components/input/full-ssn-input'
import { Legend, SubLegend } from '@contentful-components/forms/legend'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { HelpCenterScreenWhenInView } from '@lib/hooks/useHelpCenterScreen'
import type { KycFormValues } from '@lib/utils/kyc'

/**
 * This form is for address validation and autocomplete.
 */
export const EnterFullSsn: FunctionComponent<Props> = ({
    title,
    subtitle,
    renderSubmit,
}) => {
    const {
        control,
        formState: { errors, submitCount },
    } = useFormContext<KycFormValues>()
    const { errorTexts, label, placeholder } = useField(CustomFieldId.KycSsn)
    const {
        field,
        fieldState: { invalid },
    } = useController({
        control,
        name: 'ssn',
        rules: {
            required: {
                value: true,
                message: errorTexts.required,
            },
            minLength: {
                value: SSN_LENGTH,
                message: errorTexts.minLength,
            },
            maxLength: {
                value: SSN_LENGTH,
                message: errorTexts.maxLength,
            },
        },
    })

    return (
        <HelpCenterScreenWhenInView screen={IntercomScreen.KYC_ENTER_SSN}>
            <Legend
                preset='default'
                customFieldsetId={CustomFieldsetId.KycEnterFullSsn}
            >
                {title}
            </Legend>
            <SubLegend>{subtitle}</SubLegend>
            <FullSsnInput
                {...field}
                size='medium'
                preset='material'
                validationCount={submitCount}
                valid={!invalid}
                error={<ErrorMessage errors={errors} name='ssn' />}
                label={label}
                data-testid='kyc.fullSsn.input'
                placeholder={placeholder}
            />
            {renderSubmit(control)}
        </HelpCenterScreenWhenInView>
    )
}

type Props = FormProps<KycFormValues>
